import { useState } from 'react'
import { MobilePOSHeader } from './MobilePOSHeader'
import { MobileProductSearch } from './MobileProductSearch'
import { MobileCartTable } from './MobileCartTable'
import { MobileFloatingCheckout } from './MobileFloatingCheckout'
import { MobileSaleCompletion } from './MobileSaleCompletion'

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface CartItem {
  id: string
  name: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
  gst_rate?: number
}

interface MobilePOSLayoutProps {
  // Cart data
  cart: CartItem[]
  onUpdateQuantity: (productId: string, quantity: number) => void
  onRemoveFromCart: (productId: string) => void
  onClearCart: () => void
  highlightedItems: Set<string>
  
  // Products
  products: Product[]
  onAddToCart: (product: Product) => void
  onQuickAdd?: () => void

  // Customers
  customers: Customer[]
  selectedCustomer: Customer | null
  onCustomerChange: (customerId: string) => void
  onAddCustomer: () => void
  
  // Payment
  paymentMethod: 'cash' | 'card' | 'upi' | 'split'
  setPaymentMethod: (method: 'cash' | 'card' | 'upi' | 'split') => void
  amountReceived: string
  setAmountReceived: (amount: string) => void
  
  // Calculations
  cartSubtotal: number
  taxAmount: number
  finalTotal: number
  formatCurrency: (amount: number) => string
  
  // Sales operations
  onProcessSale: () => void
  canCompleteSale: boolean
  onPreviewBill: () => void
  
  // Advanced features
  onApplyItemDiscount: (itemId: string, type: 'fixed' | 'percentage', value: number) => void
  onRemoveItemDiscount: (itemId: string) => void
  onOverridePrice: (itemId: string, price: number) => void
  
  // Header props
  suspendedSalesCount: number
  onSuspendSale: () => void
  onShowSuspendedSales: () => void
  recentOrders: any[]
  onReprintReceipt: (orderId: string) => void
  onViewOrder: (orderId: string) => void
  onSettings?: () => void
  onReports?: () => void
  onUserManagement?: () => void
  
  // Sale completion props
  saleCompletionData?: {
    isVisible: boolean
    billNumber: string
    customer?: Customer | null
    totalAmount: number
    change: number
  } | null
  onPrintReceipt?: () => void
  onSkipPrint?: () => void
  onNewSale?: () => void
}

export const MobilePOSLayout = ({
  cart,
  onUpdateQuantity,
  onRemoveFromCart,
  onClearCart,
  highlightedItems,
  products,
  onAddToCart,
  onQuickAdd,
  customers,
  selectedCustomer,
  onCustomerChange,
  onAddCustomer,
  paymentMethod,
  setPaymentMethod,
  amountReceived,
  setAmountReceived,
  cartSubtotal,
  taxAmount,
  finalTotal,
  formatCurrency,
  onProcessSale,
  canCompleteSale,
  onPreviewBill,
  onApplyItemDiscount,
  onRemoveItemDiscount,
  onOverridePrice,
  suspendedSalesCount,
  onSuspendSale,
  onShowSuspendedSales,
  recentOrders,
  onReprintReceipt,
  onViewOrder,
  onSettings,
  onReports,
  onUserManagement,
  saleCompletionData,
  onPrintReceipt,
  onSkipPrint,
  onNewSale
}: MobilePOSLayoutProps) => {
  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Mobile Header */}
      <MobilePOSHeader
        hasActiveCart={cart.length > 0}
        suspendedSalesCount={suspendedSalesCount}
        onSuspendSale={onSuspendSale}
        onShowSuspendedSales={onShowSuspendedSales}
        recentOrders={recentOrders}
        onReprintReceipt={onReprintReceipt}
        onViewOrder={onViewOrder}
        formatCurrency={formatCurrency}
        onSettings={onSettings}
        onReports={onReports}
        onUserManagement={onUserManagement}
      />

      {/* Fixed Product Search */}
      <div className="sticky top-0 z-30 bg-gray-50 dark:bg-gray-900 p-2 border-b border-gray-200 dark:border-gray-700">
        <MobileProductSearch
          products={products}
          onAddToCart={onAddToCart}
          formatCurrency={formatCurrency}
          onQuickAdd={onQuickAdd}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-2 pb-24">
        {/* Cart Table */}
        <MobileCartTable
          cart={cart}
          onUpdateQuantity={onUpdateQuantity}
          onRemoveFromCart={onRemoveFromCart}
          onClearCart={onClearCart}
          formatCurrency={formatCurrency}
          highlightedItems={highlightedItems}
          onApplyItemDiscount={onApplyItemDiscount}
          onRemoveItemDiscount={onRemoveItemDiscount}
          onOverridePrice={onOverridePrice}
        />
      </div>

      {/* Floating Checkout */}
      <MobileFloatingCheckout
        cart={cart}
        cartSubtotal={cartSubtotal}
        taxAmount={taxAmount}
        finalTotal={finalTotal}
        formatCurrency={formatCurrency}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        amountReceived={amountReceived}
        setAmountReceived={setAmountReceived}
        onProcessSale={onProcessSale}
        canCompleteSale={canCompleteSale}
        onPreviewBill={onPreviewBill}
        customers={customers}
        selectedCustomer={selectedCustomer}
        onCustomerChange={onCustomerChange}
        onAddCustomer={onAddCustomer}
      />
      
      {/* Sale Completion Modal */}
      <MobileSaleCompletion
        saleCompletionData={saleCompletionData}
        onPrintReceipt={onPrintReceipt || (() => {})}
        onSkipPrint={onSkipPrint || (() => {})}
        onNewSale={onNewSale || (() => {})}
        formatCurrency={formatCurrency}
      />
    </div>
  )
}